/**
 * Envizion Brand Logo Configuration
 *
 * This file defines all logo assets and their variants based on the
 * Envizion Brand Guidelines. Logos are organized by type and color variant.
 *
 * Logo Types:
 * - Envizion: Full "Envizion" wordmark
 * - AVA: Abbreviated "AVA" logo for compact spaces
 *
 * Color Variants (matching brand palette):
 * - Emerald (#00df82): Primary brand color - for light backgrounds
 * - Mint (#2cc295): Secondary - alternative for light backgrounds
 * - CastletonGreen (#03624c): Deep green - for light backgrounds
 * - RichBlack (#021a1a): Dark - for light backgrounds
 * - White (#ffffff): For dark backgrounds
 * - AntiFlashWhite (#f1f7f7): Subtle white - for dark backgrounds
 */

import { LogoVariant } from './enums';

// Base path for brand logos
const BRAND_LOGO_PATH = '/img/brand';

/**
 * Envizion full wordmark logos
 */
export const ENVIZION_LOGOS = {
	emerald: `${BRAND_LOGO_PATH}/Envizion_Logo_Emerald_LightGreen.svg`,
	mint: `${BRAND_LOGO_PATH}/Envizion_Logo_Mint.svg`,
	castletonGreen: `${BRAND_LOGO_PATH}/Envizion_Logo_CastletonGreen.svg`,
	richBlack: `${BRAND_LOGO_PATH}/Envizion_Logo_RichBlack.svg`,
	white: `${BRAND_LOGO_PATH}/Envizion_Logo_White.svg`,
	antiFlashWhite: `${BRAND_LOGO_PATH}/Envizion_Logo_Anti-Flash-White.svg`,
};

/**
 * Envizion brandmarks (the "E" symbol, used in branded panels with slogan)
 */
export const ENVIZION_BRANDMARKS = {
	emerald: `${BRAND_LOGO_PATH}/Envizion_Brandmark_Emerald.svg`,
	white: `${BRAND_LOGO_PATH}/Envizion_Brandmark_White.svg`,
	castletonGreen: `${BRAND_LOGO_PATH}/Envizion_Brandmark_CastletonGreen.svg`,
};

/**
 * AVA abbreviated logos (for sidebar, compact spaces)
 */
export const AVA_LOGOS = {
	emerald: `${BRAND_LOGO_PATH}/AVA_Logo_Emerald_LightGreen.svg`,
	mint: `${BRAND_LOGO_PATH}/AVA_Logo_Mint.svg`,
	castletonGreen: `${BRAND_LOGO_PATH}/AVA_Logo_CastletonGreen.svg`,
	richBlack: `${BRAND_LOGO_PATH}/AVA_Logo_RichBlack.svg`,
	white: `${BRAND_LOGO_PATH}/AVA_Logo_White.svg`,
	antiFlashWhite: `${BRAND_LOGO_PATH}/AVA_Logo_Anti-Flash-White.svg`,
};

/**
 * Map LogoVariant enum to logo paths
 * This maintains backward compatibility with existing code
 */
export const LOGO_VARIANT_MAP = {
	// Envizion logos
	envizion: {
		[LogoVariant.COLORED]: ENVIZION_LOGOS.castletonGreen,
		[LogoVariant.WHITE]: ENVIZION_LOGOS.white,
		[LogoVariant.BLACK]: ENVIZION_LOGOS.richBlack,
	},
	// AVA logos
	ava: {
		[LogoVariant.COLORED]: AVA_LOGOS.castletonGreen,
		[LogoVariant.WHITE]: AVA_LOGOS.white,
		[LogoVariant.BLACK]: AVA_LOGOS.richBlack,
	},
};

/**
 * Get logo path based on type and variant
 * @param {string} type - 'envizion' or 'ava'
 * @param {number} variant - LogoVariant enum value
 * @returns {string} Path to logo file
 */
export function getLogoPath(type = 'ava', variant = LogoVariant.COLORED) {
	const logoType = LOGO_VARIANT_MAP[type] || LOGO_VARIANT_MAP.ava;
	return logoType[variant] || logoType[LogoVariant.COLORED];
}

/**
 * Get appropriate logo for current theme
 * @param {string} type - 'envizion' or 'ava'
 * @param {boolean} isDark - Whether dark theme is active
 * @returns {string} Path to logo file
 */
export function getThemedLogo(type = 'ava', isDark = false) {
	if (isDark) {
		// Use Mint variant on dark backgrounds for visibility
		const logos = type === 'envizion' ? ENVIZION_LOGOS : AVA_LOGOS;
		return logos.mint;
	}
	return getLogoPath(type, LogoVariant.COLORED);
}

/**
 * Logo metadata for Design System documentation
 */
export const LOGO_METADATA = {
	envizion: {
		name: 'Envizion',
		description: 'Full Envizion wordmark for headers, login pages, and prominent branding',
		variants: [
			{ id: 'emerald', name: 'Emerald (Primary)', path: ENVIZION_LOGOS.emerald, background: 'light' },
			{ id: 'mint', name: 'Mint', path: ENVIZION_LOGOS.mint, background: 'light' },
			{ id: 'castletonGreen', name: 'Castleton Green', path: ENVIZION_LOGOS.castletonGreen, background: 'light' },
			{ id: 'richBlack', name: 'Rich Black', path: ENVIZION_LOGOS.richBlack, background: 'light' },
			{ id: 'white', name: 'White', path: ENVIZION_LOGOS.white, background: 'dark' },
			{ id: 'antiFlashWhite', name: 'Anti-Flash White', path: ENVIZION_LOGOS.antiFlashWhite, background: 'dark' },
		],
	},
	ava: {
		name: 'AVA',
		description: 'Abbreviated logo for sidebar, favicon, and compact spaces',
		variants: [
			{ id: 'emerald', name: 'Emerald (Primary)', path: AVA_LOGOS.emerald, background: 'light' },
			{ id: 'mint', name: 'Mint', path: AVA_LOGOS.mint, background: 'light' },
			{ id: 'castletonGreen', name: 'Castleton Green', path: AVA_LOGOS.castletonGreen, background: 'light' },
			{ id: 'richBlack', name: 'Rich Black', path: AVA_LOGOS.richBlack, background: 'light' },
			{ id: 'white', name: 'White', path: AVA_LOGOS.white, background: 'dark' },
			{ id: 'antiFlashWhite', name: 'Anti-Flash White', path: AVA_LOGOS.antiFlashWhite, background: 'dark' },
		],
	},
};

export default {
	ENVIZION_LOGOS,
	ENVIZION_BRANDMARKS,
	AVA_LOGOS,
	LOGO_VARIANT_MAP,
	LOGO_METADATA,
	getLogoPath,
	getThemedLogo,
};
